import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import { XCircle, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

const CONFLICTS = [
  {
    art: "Art. 13",
    title: "Transparency &amp; information",
    text: "Black-box ML can&rsquo;t explain why an alert was raised. Sentinel surfaces the drivers, cited.",
  },
  {
    art: "Art. 14",
    title: "Human oversight",
    text: "Sentinel is advisory only — the officer decides every time, with the rationale in hand.",
  },
  {
    art: "Art. 15",
    title: "Accuracy &amp; robustness",
    text: "Drift and silent failures are intrinsic to ML. Sentinel&rsquo;s outputs are reproducible.",
  },
  {
    art: "Art. 12",
    title: "Record-keeping",
    text: "Sentinel&rsquo;s ledger replays every assessment, exactly.",
  },
];

const BlackBox = () => {
  return (
    <section
      data-testid="blackbox-section"
      id="black-box"
      className="dark-section relative overflow-hidden py-24 lg:py-32 border-t border-white/10"
    >
      <div className="absolute inset-0 bg-dot-grid opacity-40" />
      {/* Diagonal accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 90% 10%, rgba(255,59,48,0.10), transparent 60%), radial-gradient(ellipse 60% 80% at 10% 90%, rgba(0,229,255,0.08), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-8">
            <SectionLabel index="07" label="The black-box problem" dark />
            <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[64px] tracking-tight leading-[1.02]">
              Black-box AI is in direct{" "}
              <span className="relative inline-block">
                <span className="relative z-10">conflict</span>
                <span className="absolute -bottom-1 left-0 right-0 h-[10px] bg-[#ff3b30]/40" aria-hidden="true" />
              </span>
              <br />
              with the EU AI Act.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:self-end">
            <p className="text-white/65 text-[17px] leading-relaxed">
              From 2 August 2026, AI used in financial risk, fraud and AML is{" "}
              <span className="text-white font-medium">high-risk</span> under
              the EU AI Act. Sentinel was designed for that bar.
            </p>
          </div>
        </div>

        <EditorialBanner
          gradient="gold-rose"
          image="https://images.unsplash.com/photo-1583321500900-82807e458f3c?auto=format&fit=crop&w=2400&q=80"
          kicker="EU AI Act compliance"
          headline="Designed for the bar — not retrofitted to it."
          chips={[
            { c: "#f43f5e", t: "Black-box ✕" },
            { c: "#00e5ff", t: "Deterministic ✓" },
            { c: "#f59e0b", t: "Cited ✓" },
          ]}
          testId="blackbox-banner"
        />

        {/* Big VS comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-px">
          {/* Black-box */}
          <div className="bg-[#13141c] p-8 lg:p-10 relative" data-testid="blackbox-card-bad">
            <div className="flex items-center gap-3 mb-6">
              <XCircle className="w-5 h-5 text-[#ff3b30]" strokeWidth={2} />
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#ff3b30]">
                Black-box ML — non-compliant by design
              </span>
            </div>
            <p className="font-display font-bold text-white text-[28px] lg:text-[34px] tracking-tight leading-tight">
              &ldquo;The model said so.&rdquo;
            </p>
            <ul className="mt-7 space-y-3 text-white/65 text-[14.5px]">
              {[
                "Outputs can't be explained to a board or supervisor",
                "Same input can yield different outputs after a silent retrain",
                "Drift and bias hide inside opaque weights",
                "Human oversight becomes a rubber stamp",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#ff3b30] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 inline-flex items-center gap-2 px-3 py-2 border border-[#ff3b30]/30 bg-[#ff3b30]/5 text-[#ff3b30]">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
                Violates EU AI Act Art. 12&ndash;15
              </span>
            </div>
          </div>

          {/* Sentinel */}
          <div className="bg-[#0d0e16] p-8 lg:p-10 relative" data-testid="blackbox-card-good">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-5 h-5 text-[#00e5ff]" strokeWidth={2} />
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#00e5ff]">
                Sentinel — deterministic, advisory, defensible
              </span>
            </div>
            <p className="font-display font-bold text-white text-[28px] lg:text-[34px] tracking-tight leading-tight">
              &ldquo;Here is exactly why — and the article it&rsquo;s cited to.&rdquo;
            </p>
            <ul className="mt-7 space-y-3 text-white/80 text-[14.5px]">
              {[
                "Zero black-box models in the scoring path",
                "Same inputs = same output, every time",
                "Drivers cited to regulation",
                "Officer always decides — Sentinel never blocks",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#00e5ff] shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: b }} />
                </li>
              ))}
            </ul>
            <div className="mt-8 inline-flex items-center gap-2 px-3 py-2 border border-[#00e5ff]/30 bg-[#00e5ff]/5 text-[#00e5ff]">
              <FileText className="w-3.5 h-3.5" />
              <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
                Aligned with EU AI Act Art. 12, 13, 14, 15
              </span>
            </div>
          </div>
        </div>

        {/* Article-by-article mapping */}
        <p className="mt-12 mb-6 font-mono text-[11px] tracking-[0.22em] uppercase text-white/45">
          How each EU AI Act article maps to Sentinel
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {CONFLICTS.map((c, i) => (
            <div
              key={c.art}
              data-testid={`eu-ai-act-art-${i}`}
              className="bg-[#0d0e16] p-7 min-h-[210px] flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#00e5ff]">
                  EU AI Act
                </span>
                <span className="font-display font-bold text-white text-[18px] tracking-tight">
                  {c.art}
                </span>
              </div>
              <p
                className="font-display font-semibold text-white text-[15px] tracking-tight"
                dangerouslySetInnerHTML={{ __html: c.title }}
              />
              <p className="text-white/65 text-[13.5px] leading-relaxed mt-auto">
                {c.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlackBox;
